import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

type RoleKey = "dept_head" | "technical_director" | "coo";
const ROLE_LABELS: Record<RoleKey, string> = {
  dept_head: "Department Head",
  technical_director: "Technical Director",
  coo: "COO (required when est. cost > $50,000)",
};

export default function AdminEarMarApprovers() {
  const [users, setUsers] = useState<{ user_id: string; full_name: string | null }[]>([]);
  const [picks, setPicks] = useState<Record<RoleKey, string>>({ dept_head: "", technical_director: "", coo: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [{ data: profs }, { data: cfg }] = await Promise.all([
        supabase.from("profiles").select("user_id, full_name").order("full_name"),
        supabase.from("ear_mar_approver_config").select("*"),
      ]);
      setUsers((profs || []) as any);
      const next: any = { dept_head: "", technical_director: "", coo: "" };
      (cfg || []).forEach((c: any) => { next[c.role] = c.user_id; });
      setPicks(next);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    const rows = (Object.keys(picks) as RoleKey[])
      .filter(r => picks[r])
      .map(r => ({ role: r, user_id: picks[r], updated_at: new Date().toISOString() }));
    const { error } = await supabase.from("ear_mar_approver_config").upsert(rows, { onConflict: "role" });
    setSaving(false);
    if (error) toast.error(error.message); else toast.success("Approvers saved");
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">EAR / MAR Approvers</h1>
        <p className="text-sm text-muted-foreground">Pick the specific user holding each approval role. New projects auto-populate the chain from this config.</p>
      </div>
      <Card className="max-w-2xl"><CardContent className="p-6 space-y-4">
        {(Object.keys(ROLE_LABELS) as RoleKey[]).map(role => (
          <div key={role} className="space-y-2">
            <Label>{ROLE_LABELS[role]}</Label>
            <Select value={picks[role]} onValueChange={(v) => setPicks(p => ({ ...p, [role]: v }))}>
              <SelectTrigger><SelectValue placeholder="Select user…" /></SelectTrigger>
              <SelectContent>
                {users.map(u => (
                  <SelectItem key={u.user_id} value={u.user_id}>{u.full_name || u.user_id.slice(0, 8)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
        <Button onClick={save} disabled={saving} className="bg-teal hover:bg-teal/90">{saving ? "Saving…" : "Save"}</Button>
      </CardContent></Card>
    </AdminLayout>
  );
}
